import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import UserTagsContext from '../context/UserTagsContext';
import AppContext from '../context/AppContext';
import UserTagsAPI, { TagVocabItem } from '../api/UserTagsAPI';
import { analytics } from '../utils/analytics';

// Loads the controlled vocabulary (once, cached across games) and this game's current user tags,
// exposing optimistic toggles the move-scope and game-scope pickers share. Writes require auth; an
// anonymous toggle opens the login overlay instead of hitting the API.
let vocabCache: TagVocabItem[] | null = null;

export default function UserTagsProvider(props: {
  gameId: number;
  children: React.ReactNode;
}): React.ReactNode {
  const { gameId, children } = props;
  const { isAuthenticated, openLogin } = useContext(AppContext);

  const [vocabulary, setVocabulary] = useState<TagVocabItem[]>(vocabCache ?? []);
  const [moveTags, setMoveTags] = useState<Record<string, string[]>>({});
  const [gameTags, setGameTags] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (vocabCache == null) {
        try {
          vocabCache = await UserTagsAPI.getVocabulary();
        } catch {
          vocabCache = [];
        }
      }
      if (!cancelled) {
        setVocabulary(vocabCache);
      }
      if (isAuthenticated) {
        try {
          const tags = await UserTagsAPI.getGameTags(gameId);
          if (!cancelled) {
            setMoveTags(tags.MoveTags ?? {});
            setGameTags(tags.GameTags ?? []);
          }
        } catch {
          /* leave empty on failure — tagging still works, it just starts blank */
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [gameId, isAuthenticated]);

  const toggleMoveTag = useCallback(
    (pgn: string, theme: string) => {
      if (!isAuthenticated) {
        openLogin();
        return;
      }
      const current = moveTags[pgn] ?? [];
      const has = current.includes(theme);
      // Optimistic: update UI first, then fire the write; on failure, roll back.
      const next = has ? current.filter((t) => t !== theme) : [...current, theme];
      setMoveTags((m) => ({ ...m, [pgn]: next }));
      const call = has
        ? UserTagsAPI.removeMoveTag(gameId, pgn, theme)
        : UserTagsAPI.addMoveTag(gameId, pgn, theme);
      call
        .then(() => {
          if (!has) {
            analytics.track('user_tag_add', { scope: 'move', theme });
          }
        })
        .catch(() => setMoveTags((m) => ({ ...m, [pgn]: current })));
    },
    [gameId, isAuthenticated, moveTags, openLogin],
  );

  const toggleGameTag = useCallback(
    (theme: string) => {
      if (!isAuthenticated) {
        openLogin();
        return;
      }
      const has = gameTags.includes(theme);
      const next = has ? gameTags.filter((t) => t !== theme) : [...gameTags, theme];
      setGameTags(next);
      const call = has
        ? UserTagsAPI.removeGameTag(gameId, theme)
        : UserTagsAPI.addGameTag(gameId, theme);
      call
        .then(() => {
          if (!has) {
            analytics.track('user_tag_add', { scope: 'game', theme });
          }
        })
        .catch(() => setGameTags(gameTags));
    },
    [gameId, isAuthenticated, gameTags, openLogin],
  );

  const value = useMemo(
    () => ({ vocabulary, moveTags, gameTags, toggleMoveTag, toggleGameTag }),
    [vocabulary, moveTags, gameTags, toggleMoveTag, toggleGameTag],
  );

  return <UserTagsContext.Provider value={value}>{children}</UserTagsContext.Provider>;
}
